import * as React from 'react';
import { VendorViewModel } from '@models/Vendor';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { getRoute } from '@common/route-definitions';
import { RouteDefinitions } from '@common/route-definitions';
import { SpinnerPage, Breadcrumbs, Tabs, Tab } from '@common/components';
import { CommentType } from '@models/Comment';
import { CommentSearch } from 'src/comments';
import { VendorInfoComponent } from 'src/vendor/_components/vendor-info/vendor-info.component';
import { VendorInfoTradeTableComponent } from 'src/vendor/_components/vendor-info/vendor-info-trade-table.component';
import { VendorInfoDefaultRatingComponent } from 'src/vendor/_components/vendor-info/vendor-info-default-rating.component';
import { VendorTable } from '../vendor-documents/vendor-document-table.container';
import { Role, Roles } from '@models/Roles';
import { DocumentViewModel, FileViewModel } from '@models/Document';
import { VendorPacketEvents } from 'src/vendor/_components/vendor-packet/vendor-packet-events.container';
import { TradeZipsContainer } from '../vendor-trade-maintenance/trade-zips/_components/trade-zips.container';
import { TradePriceAgreement } from '../vendor-trade-maintenance/_components/trade-price-agreement.container';
import { vendorSchema } from './vendor-schema';

export interface VendorDetailsComponentProperties extends RouteComponentProps<{ id: number }> {
  readonly vendorVM: VendorViewModel;
  readonly documents: DocumentViewModel[];
  readonly getVendorDetails: (vendorId: number) => void;
  readonly clearVendor: () => void;
  readonly uploadFiles: (vendorId: number, fileVMs: FileViewModel[], vendor: VendorViewModel) => void;
  readonly removeFile: (vendorId: number, fileId: number, vendor: VendorViewModel) => void;
  readonly convertVendor: (vendor: VendorViewModel) => void;
}

interface VendorDetailsComponentState {
  convertErrors?: string[];
}

export class VendorDetailsComponent extends React.Component<
  VendorDetailsComponentProperties,
  VendorDetailsComponentState
> {
  constructor(props: VendorDetailsComponentProperties) {
    super(props);

    this.state = {
      convertErrors: []
    };
  }

  componentWillMount() {
    if (this.props.match && this.props.match.params.id) {
      const vendorId = this.props.match.params.id;
      this.props.getVendorDetails(vendorId);
    }
  }

  onUploadClick = (fileVMs: FileViewModel[], vendor: VendorViewModel) => {
    this.props.uploadFiles(this.props.match.params.id, fileVMs, vendor);
  };

  onRemoveClick = (fileId: number, vendor: VendorViewModel) => {
    this.props.removeFile(this.props.match.params.id, fileId, vendor);
  };

  requiresMoreInfo = () => {
    return false;
  };

  convertVendor = async () => {
    const { vendorVM, documents } = this.props;

    const vendorWithDocuments = {
      ...vendorVM
    };

    documents.forEach((document: any) => {
      if (document.documentType) {
        vendorWithDocuments[document.documentType.value.replace(/(')/, '')] = true;
      }
    });

    try {
      await vendorSchema.validate(vendorWithDocuments, { strict: true, stripUnknown: true, abortEarly: false });
      this.props.convertVendor({ ...vendorVM, converted: true });
    } catch (err) {
      if (err.errors) {
        this.setState({
          convertErrors: err.errors
        });
      }
    }
  };

  render() {
    if (!this.props.vendorVM) {
      return <SpinnerPage />;
    }

    return (
      <div>
        <Breadcrumbs />
        <div className="row">
          <div className="col-xs-4">
            <div className="row middle-xs">
              <h1> Vendor Details </h1>
              <h5>
                <Link to={getRoute(RouteDefinitions.vendor.Edit, { id: +this.props.match.params.id })}>Edit</Link>
              </h5>
            </div>
          </div>
          <div className="col-xs-2">
            <Link to={getRoute(RouteDefinitions.vendor.TradeMaintenace, { id: +this.props.match.params.id })}>
              <button className="btn btn-primary" type="button">
                Trade Maintenance
              </button>
            </Link>
          </div>
          <div className="col-xs-2">
            <button disabled={true} className="btn btn-primary" type="button">
              {'Zips & Trades'}
            </button>
          </div>
          <div className="col-xs-2">
            <button disabled={true} className="btn btn-primary" type="button">
              Zip Map
            </button>
          </div>
          <div className="col-xs-2">
            <button disabled={true} className="btn btn-primary" type="button">
              Portal
            </button>
          </div>
        </div>
        <VendorInfoComponent vendorVM={this.props.vendorVM} />
        <div className="row">
          <div className="col-xs-9">
            <VendorInfoTradeTableComponent />
          </div>
          <div className="col-xs-3">
            <VendorInfoDefaultRatingComponent
              invoicePricing={this.props.vendorVM.invoicePricing ? this.props.vendorVM.invoicePricing : 0}
              vendorRelations={this.props.vendorVM.vendorRelations}
              customerRelations={this.props.vendorVM.customerRelations}
            />
          </div>
        </div>
        <div className="row center-xs mt-10 pt-10">
          <Tabs>
            <Tab title={'Packets'}>
              {this.props.vendorVM.id && <VendorPacketEvents vendorId={this.props.vendorVM.id} />}
            </Tab>
            <Tab title={'Documents'}>
              <VendorTable
                vendorVm={this.props.vendorVM}
                fileList={this.props.documents}
                uploadCallback={this.onUploadClick}
                removeCallback={this.onRemoveClick}
                uploadModalTitle={'Upload Files for Vendor: ' + this.props.vendorVM.companyName}
                removeModalTitle={'Remove File for Vendor: ' + this.props.vendorVM.companyName}
                canRemove={Role.CheckRoles([Roles.Admin, Roles.IBSAdmin, Roles.IBSManager])}
              />
            </Tab>
            <Tab title={'Comments'}>
              <CommentSearch entityId={+this.props.match.params.id} commentType={CommentType.Vendor} />
            </Tab>
            <Tab title={'Covered Zips'}>
              <TradeZipsContainer vendorVM={this.props.vendorVM} readonly={true} isTab={true} keyVal="zips-info" />
            </Tab>
            <Tab title={'Price Agreements'}>
              <TradePriceAgreement id={+this.props.match.params.id} readOnly={true} isTab={true} />
            </Tab>
          </Tabs>
        </div>
        {!this.props.vendorVM.converted && (
          <>
            <div className="row center-xs mt-10 pt-10">
              <button className="btn btn-primary" disabled={this.requiresMoreInfo()} onClick={this.convertVendor}>
                Convert
              </button>
            </div>
            {this.state.convertErrors &&
              this.state.convertErrors.length > 0 && (
                <div className="row mt-10 pt-10 pb-10">
                  <ol>
                    {this.state.convertErrors.map((error: string) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ol>
                </div>
              )}
          </>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-details/vendor-details.component.tsx